import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { wireOutcomesToOutcome, WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { SlPopup } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { Temporal } from 'temporal-polyfill';
import { contexts } from '../contexts.js';
import { Linkifier } from '../linkifier.js';
import { BarChart, BarChartDatum } from './bar-chart.js';
import { linkStyles, popoverCardStyles } from './cssstyles.js';
import { type BaselineType } from './fk-commitstats.js';

type Datum = BarChartDatum & {
  commitStats: WireTypes.CommitStats,
  baseline?: number,
}

@customElement('commits-minimap')
export class CommitsMinimap extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) private _project?: ContextType<typeof contexts.project>;

  @property({ attribute: false }) commitStats?: WireTypes.CommitStats[];
  @property({ attribute: false }) baselineType?: BaselineType;
  @property({ attribute: false }) linkifier?: Linkifier<WireTypes.CommitStats|undefined>;
  @property({ attribute: false }) selectedCommitId?: string;

  @state() private _data: (Datum|{ separator: string })[] = [];
  @query('sl-popup') _popup?: SlPopup;
  @query('bar-chart') _barChart?: HTMLElement;

  protected override update(changedProperties: Map<string, any>): void {
    if (changedProperties.has('commitStats') || changedProperties.has('selectedCommitId') || changedProperties.has('baselineType'))
      this._data = this._computeData();
    super.update(changedProperties);
  }

  private _computeData() {
    if (!this._project)
      return [];
    const commitStats = this.commitStats ?? [];
    const predata: Datum[] = commitStats.filter(s => s.runsCount).map(s => ({
      outcome: wireOutcomesToOutcome(s.testStats, this._project?.acceptableFlakinessRatio ?? 0),
      value: this.baselineType === 'testruns' ? TestOutcomes.sumAllCounts(s.testStats) : s.durationMs,
      selected: s.commit.commitId === this.selectedCommitId,
      commitStats: s,
    }));
    let previousValue: number|undefined;
    for (const d of predata) {
      if (previousValue)
        d.baseline = previousValue;
      previousValue = d.value;
    }

    // Add separators
    const data = [];
    let lastDay: string|undefined;
    if (commitStats.length && commitStats.at(-1)!.runsCount === 0) {
      const stats = commitStats.at(-1)!;
      predata.push({
        selected: stats.commit.commitId === this.selectedCommitId,
        commitStats: stats,
      });
    }

    for (const datum of predata) {
      const commitDay = Temporal.Instant.fromEpochMilliseconds(datum.commitStats.commit.timestamp).toZonedDateTimeISO(this._project.timezone).toPlainDate();
      const day = commitDay.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
      })
      if (day !== lastDay)
        data.push({ separator: day });
      lastDay = day;
      data.push(datum);
    }
    return data;
  }

  @state() private _hoveredDatum?: Datum;

  private _renderPopover() {
    if (!this._hoveredDatum)
      return nothing;

    return html`
      <section class="popup popover-card">
        <fk-commitstats .stats=${this._hoveredDatum.commitStats} .baseline=${this._hoveredDatum.baseline} .baselineType=${this.baselineType}></fk-commitstats>
      </section>
    `;
  }

  override render() {
    return html`
      <sl-popup ?active=${!!this._hoveredDatum} placement=top-start distance=10 shift>
        ${this._renderPopover()}
      </sl-popup>
      <v-box class=minimap>
        <div class=title>
          <slot name=header></slot>
        </div>
        <h-box>
          <slot name=prefix></slot>
          ${BarChart.render({
            data: this._data,
            fancy: true,
            linkifier: datum => this.linkifier?.(this.selectedCommitId === datum.commitStats.commit.commitId ? undefined : datum.commitStats),
            hover: event => {
              this._hoveredDatum = event.detail.datum;
              // Anchor to the whole chart rather than the hovered bar, so the
              // card stays put while the cursor moves across bars. The card is
              // left-aligned, so a width change only moves its right edge.
              if (this._popup && this._barChart)
                this._popup.anchor = this._barChart;
            },
            leave: () => {
              this._hoveredDatum = undefined;
            }
          })}
        </h-box>
      </v-box>
    `;
  }

  static styles = [linkStyles, popoverCardStyles, css`
    .minimap {
    }

    sl-popup {
      --arrow-color: var(--sl-color-primary-200);
    }

    .popup {
      pointer-events: none;
      overflow: hidden;
      margin: 0 var(--sl-spacing-medium);

      .more-commits {
        background: var(--sl-color-primary-100);
        display: flex;
        align-items: center;
        justify-content: center;
        border-top: 1px solid var(--fk-color-border);
        font-weight: var(--sl-font-weight-semibold);
        padding: var(--sl-spacing-small);
      }

      fk-commitstats {
        grid-column: 1/-1;
        border: none;
        border-radius: 0;
      }

      fk-commitstats + fk-commitstats {
        border-top: 1px solid var(--fk-color-border);
      }

    }

    bar-chart {
      height: 60px;
      min-width: 0;
      border-bottom: 1px solid var(--fk-color-border);
    }

    .title {
      font-size: 13px;
      color: var(--sl-color-neutral-500);
    }


    .expected {
      --color: var(--fk-color-outcome-expected);
    }
    .flaked {
      --color: var(--fk-color-outcome-flaked);
    }
    .regressed {
      --color: var(--fk-color-outcome-regressed);
    }
    .skipped {
      --color: var(--fk-color-outcome-skipped);
    }
    .unexpected {
      --color: var(--fk-color-outcome-unexpected);
    }
    .idle, .untested {
      --color: transparent;
    }
  `];
}
